import { noChange } from 'lit'
import { AsyncDirective, directive } from 'lit/async-directive.js'
import { ElementPart, PartType } from 'lit/directive.js'
import {
	BehaviorSubject,
	EMPTY,
	Observable,
	Subject,
	animationFrames,
	concat,
	debounceTime,
	filter,
	first,
	fromEvent,
	map,
	merge,
	skip,
	switchMap,
	take,
	takeUntil,
	tap,
	timer,
} from 'rxjs'
import { fromResizeObserver } from './layout'
import { reducedMotion$ } from './reduced-motion'

export interface OverflowWithinEvent extends CustomEvent<{
	scrollTop: number
	scrollHeight: number
	clientHeight: number
	scrollLeft: number
	scrollWidth: number
	clientWidth: number
	e: Event
}> {}

export interface OverflowWithinCommandEvent extends CustomEvent<{
	name: string
	action: 'scrollTo'
	top: number
	left?: number
}> {}

export interface OverflowWithinOptions {
	/** Hide native scrollbars while keeping scroll behavior intact. */
	hide?: boolean
	/** Which axis can scroll. Default `'both'`. */
	direction?: 'vertical' | 'horizontal' | 'both'
	/**
	 * Optional name for cross-component control via the global
	 * `@schmancy:scrollTo` event.
	 */
	name?: string
	/** Debounce in ms for the dispatched `scroll` event. Default `10`. */
	debounce?: number
	/**
	 * Keeps something in view by scrolling it to the nearest edge along each
	 * axis the region scrolls — honouring `scroll-padding` and the writing
	 * direction, the start winning when it is wider than the region. Focus never
	 * moves, and nothing but the region scrolls.
	 *
	 * - a key: whenever it changes (and whenever the region resizes), the union
	 *   of the region's `[data-reveal]` descendants — a selection and its ⋮.
	 * - `'focus'`: whenever focus lands inside the region, the closest
	 *   `[data-reveal]` around the focused element, else that element.
	 * - `null` / `undefined`: off.
	 */
	reveal?: unknown
}

const SCROLLBAR_HIDE_CLASS = 'schmancy-overflow-within-hidden'
let stylesheetInstalled = false

// DEV-only structural guard. All state below is gated behind
// `import.meta.env.DEV` so production allocates nothing and ships none of
// this — same idiom as `state/active-host.ts`'s `_devSlot`.
//
// The failure this catches: a definite-height (percentage or flex-basis)
// chain reaching past the element that should be the scroll boundary, down
// to a descendant that's squeezed smaller than its own content (typically
// via `min-h-0` on a flex/grid item) with no `overflow` of its own to catch
// the excess. That excess renders past the descendant's box as ink — real
// pixels, unreachable by any scrollbar in the subtree — because a flex/grid
// item's flex-resolved height is its OWN final layout box, not a soft cap;
// the universal, ubiquitous fix for "make a nested flex item scroll" is
// `overflow-y: auto` on THAT item itself, never on a distant ancestor, which
// is exactly the fact this guard is built to enforce.
const warnedElements: WeakSet<Element> | undefined = import.meta.env.DEV ? new WeakSet<Element>() : undefined

/**
 * True when the element's own block-end overflow survives after every
 * out-of-flow descendant is discounted.
 *
 * `scrollHeight` counts absolutely-positioned descendants, and an out-of-flow
 * box never contributed to its ancestor's height in the first place — so it
 * cannot be content the ancestor was squeezed below, which is the only failure
 * this guard is built to catch. An M3 control makes the distinction concrete:
 * `<md-checkbox>` is an 18px box deliberately carrying a 48px absolutely-
 * positioned touch target and a 40px state layer, so it reads 18/33 while
 * clipping nothing. Both of the guard's prescribed fixes actively damage such
 * an element — `overflow-y: auto` on it would clip its own state layer and put
 * a scrollbar on an 18px box, and there is no definite height above it to
 * remove.
 *
 * Descends only through in-flow subtrees, and stops at any child that clips
 * its own content, whose descendants therefore cannot cross this boundary
 * however far their unclipped boxes reach.
 */
function hasInFlowOverflow(el: Element): boolean {
	const boundary = el.getBoundingClientRect().top + el.clientHeight + 1
	const crossesBoundary = (root: ParentNode): boolean => {
		for (const child of Array.from(root.children)) {
			const style = getComputedStyle(child)
			if (style.position === 'absolute' || style.position === 'fixed') continue
			if (child.getBoundingClientRect().bottom > boundary) return true
			if (style.overflowY !== 'visible') continue
			if (crossesBoundary(child)) return true
			if (child.shadowRoot && crossesBoundary(child.shadowRoot)) return true
		}
		return false
	}
	return crossesBoundary(el) || (el.shadowRoot !== null && crossesBoundary(el.shadowRoot))
}

/**
 * Walks `root`'s descendants looking for the first one whose own rendered
 * content (`scrollHeight`) exceeds its own box (`clientHeight`) by at least one
 * line while it computes `overflow-y: visible` — content escaping a box with
 * nothing to catch it. Pruned at the first descendant that already establishes
 * its own overflow handling (`overflow-y` isn't `visible`): that branch owns its
 * own scrolling and whatever is inside it is out of scope here, not a false
 * positive to chase into. A descendant whose excess is entirely out-of-flow
 * (`hasInFlowOverflow`) is not the culprit but is still descended into — its
 * own children can carry the real thing.
 *
 * The one-line floor is what separates a squeeze from ordinary typography. A
 * `line-height` tighter than the font's natural inline box — every Material
 * display and headline token is — spills half the difference past each edge of
 * the line box, and `scrollHeight` counts the bottom half. That spill is
 * unclipped ink at `overflow-y: visible`, already painted and reachable, and it
 * scales with font size, so a fixed pixel floor trips on every large heading in
 * the app. A genuine squeeze always costs at least one whole line or block box,
 * which is also the smallest excess a reader could have lost anything to.
 */
function findUnreachableOverflow(root: Element): Element | null {
	for (const child of Array.from(root.children)) {
		const style = getComputedStyle(child)
		if (style.overflowY !== 'visible') continue
		const oneLine = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.2
		if (child.clientHeight > 0 && child.scrollHeight - child.clientHeight >= oneLine && hasInFlowOverflow(child)) {
			return child
		}
		const found = findUnreachableOverflow(child)
		if (found) return found
	}
	return null
}

function devWarnUnreachableOverflow(el: HTMLElement): void {
	if (!import.meta.env.DEV) return
	const culprit = findUnreachableOverflow(el)
	if (!culprit || warnedElements!.has(culprit)) return
	warnedElements!.add(culprit)
	console.warn(
		`[schmancy] overflowWithin() region has content it can never reach: <${culprit.tagName.toLowerCase()}>` +
			` renders ${culprit.scrollHeight}px of content inside a ${culprit.clientHeight}px box with` +
			` overflow-y: visible — the excess is clipped, not scrolled, and nothing in this subtree can` +
			` reach it. This is almost always a definite-height (h-full / height: 100%) flex or grid chain` +
			` reaching past where it should stop: give THIS element its own overflowWithin() (or` +
			` overflow-y: auto), or remove the definite height above it so it can grow to its content and` +
			` let this outer scroll region reach it. See PAGE_GEOMETRY_IS_DIRECTIVE_OWNED in the schmancy skill.`,
		culprit,
	)
}

function fromMutations(el: Element, init: MutationObserverInit): Observable<MutationRecord[]> {
	return new Observable(subscriber => {
		const observer = new MutationObserver(records => subscriber.next(records))
		observer.observe(el, init)
		return () => observer.disconnect()
	})
}

function installHideStylesheet() {
	if (stylesheetInstalled) return
	stylesheetInstalled = true
	const sheet = new CSSStyleSheet()
	sheet.replaceSync(
		`.${SCROLLBAR_HIDE_CLASS} { scrollbar-width: none; -ms-overflow-style: none; }
		 .${SCROLLBAR_HIDE_CLASS}::-webkit-scrollbar { display: none; }`,
	)
	document.adoptedStyleSheets = [...document.adoptedStyleSheets, sheet]
}

/**
 * How far the scroll position moves along one axis so `[start, end]` lands
 * inside `[portStart, portEnd]`, all in logical coordinates that grow toward
 * the axis' end. Nearest edge, and never past the start: a union wider than
 * the port shows its start, which is where its name is.
 */
function nearest(start: number, end: number, portStart: number, portEnd: number): number {
	if (start < portStart) return start - portStart
	if (end > portEnd) return Math.min(end - portEnd, start - portStart)
	return 0
}

/** A scroll-padding length; `auto` reserves nothing, a percentage is of the port. */
function paddingPx(value: string, port: number): number {
	return value.endsWith('%') ? (parseFloat(value) * port) / 100 : parseFloat(value) || 0
}

/**
 * min-height / min-width: 0 so the container can clip inside grid/flex
 * cells — otherwise a flex/grid item's intrinsic content size wins over the
 * overflow container and nothing scrolls.
 *
 * Apply directly at the element where overflow should be contained; no
 * wrapper element needed — the consumer's own grid/flex container can be
 * the overflow boundary.
 */
class OverflowWithinDirective extends AsyncDirective {
	private element: HTMLElement | null = null
	private disconnecting$ = new Subject<void>()
	private currentName?: string
	private debounce = 10
	private direction: OverflowWithinOptions['direction'] = 'both'
	private revealKey$ = new BehaviorSubject<unknown>(undefined)

	render(options?: OverflowWithinOptions) {
		void options
		return noChange
	}

	override update(part: ElementPart, [options = {}]: [OverflowWithinOptions?]) {
		if (part.type !== PartType.ELEMENT) {
			throw new Error('overflowWithin directive can only be used on elements')
		}
		const el = part.element as HTMLElement
		const { hide = false, direction = 'both', name, debounce = 10, reveal } = options
		this.direction = direction
		this.debounce = debounce

		const s = el.style
		s.minHeight = '0'
		s.minWidth = '0'
		s.boxSizing = 'border-box'
		// An overflow container clips only the descendants it is the containing
		// block for, so a static one lets an absolutely positioned descendant land
		// outside it and grow an ancestor's scroll area past any reachable gesture.
		s.position = 'relative'
		s.overflowY = direction === 'horizontal' ? 'hidden' : 'auto'
		s.overflowX = direction === 'vertical' ? 'hidden' : 'auto'
		// Contain scroll-chaining on the axis this box actually scrolls, and only
		// that one: `overflow: hidden` still makes an element a scroll container on
		// its axis, so a blanket `contain` is the body-scroll-lock trick fired by
		// accident — on a horizontal rail a vertical touch drag resolves to the
		// rail, finds no room (scrollHeight === clientHeight), and, chaining
		// blocked, never reaches the page. The page stops scrolling wherever a
		// finger lands on the rail. The hidden axis stays `auto` so the gesture
		// passes through to whatever scrolls behind it.
		//
		// The contained axis reads a var() so a container embedding this region in
		// a longer scrolling page can relax chaining across its whole subtree: a
		// custom property inherits through shadow roots AND slots, reaching nested
		// scroll regions that never see this call. Longhands, not the shorthand —
		// a var() in a shorthand makes both longhands pending-substitution values,
		// which round-trips badly through CSSOM.
		s.overscrollBehaviorX = direction === 'vertical' ? 'auto' : 'var(--schmancy-overscroll-behavior, contain)'
		s.overscrollBehaviorY = direction === 'horizontal' ? 'auto' : 'var(--schmancy-overscroll-behavior, contain)'

		if (hide) {
			installHideStylesheet()
			el.classList.add(SCROLLBAR_HIDE_CLASS)
		} else {
			el.classList.remove(SCROLLBAR_HIDE_CLASS)
		}

		const isNewElement = this.element !== el
		const isNameChange = this.currentName !== name

		if (isNewElement) {
			this.element = el
			this.subscribe(debounce, name)
		} else if (isNameChange) {
			this.disconnecting$.next()
			this.disconnecting$ = new Subject<void>()
			this.subscribe(debounce, name)
		}
		this.currentName = name
		if (reveal !== this.revealKey$.value) this.revealKey$.next(reveal)

		return noChange
	}

	private subscribe(debounce: number, name: string | undefined) {
		if (!this.element) return
		const el = this.element

		// The element raises its own `scroll` below, and an inner scroller's bubbles here; only a real scroll is news.
		fromEvent(el, 'scroll', { passive: true })
			.pipe(
				filter(event => !(event instanceof CustomEvent)),
				debounceTime(debounce),
				takeUntil(this.disconnecting$),
			)
			.subscribe(e => {
				el.dispatchEvent(
					new CustomEvent('scroll', {
						detail: {
							scrollTop: el.scrollTop,
							scrollHeight: el.scrollHeight,
							clientHeight: el.clientHeight,
							scrollLeft: el.scrollLeft,
							scrollWidth: el.scrollWidth,
							clientWidth: el.clientWidth,
							e,
						},
						bubbles: true,
						composed: true,
					}) as OverflowWithinEvent,
				)
			})

		if (name !== undefined) {
			fromEvent<OverflowWithinCommandEvent>(window, '@schmancy:scrollTo')
				.pipe(
					filter(evt => evt.detail.name === name && evt.detail.action === 'scrollTo'),
					takeUntil(this.disconnecting$),
				)
				.subscribe(evt => {
					const options: ScrollToOptions = {
						behavior: reducedMotion$.value ? 'instant' : 'smooth',
						top: evt.detail.top,
					}
					if (typeof evt.detail.left === 'number') options.left = evt.detail.left
					el.scrollTo(options)
				})
		}

		const marked = () =>
			Array.from(el.querySelectorAll('[data-reveal]'), node => node.getBoundingClientRect()).filter(
				rect => rect.width > 0 || rect.height > 0,
			)
		const nextFrame = animationFrames().pipe(take(1))

		merge(
			reducedMotion$.pipe(tap(reduced => (el.style.scrollBehavior = reduced ? 'auto' : 'smooth'))),
			this.revealKey$.pipe(
				switchMap(key =>
					key == null
						? EMPTY
						: key === 'focus'
							? // Waits out the frame focus lands in, so the reveal comes after the
								// browser's own focus scroll rather than under it.
								fromEvent(el, 'focusin').pipe(
									map(event => event.target),
									filter((target): target is Element => target instanceof Element && target !== el),
									switchMap(target =>
										animationFrames().pipe(
											skip(1),
											take(1),
											map(() => {
												const unit = target.closest('[data-reveal]')
												return [(unit && unit !== el && el.contains(unit) ? unit : target).getBoundingClientRect()]
											}),
										),
									),
								)
							: // A key reveals once its marks exist — a ResizeObserver reports once
								// on observe, after the render that changed the key has laid out;
								// marks that render later arrive as mutations — then on every resize.
								concat(
									merge(
										fromResizeObserver(el).pipe(take(1)),
										fromMutations(el, {
											childList: true,
											subtree: true,
											attributes: true,
											attributeFilter: ['data-reveal'],
										}).pipe(switchMap(() => nextFrame)),
									).pipe(
										map(marked),
										first(rects => rects.length > 0),
									),
									fromResizeObserver(el).pipe(skip(1), map(marked)),
								),
				),
				filter(rects => rects.length > 0),
				map(rects => {
					const left = Math.min(...rects.map(rect => rect.left))
					const right = Math.max(...rects.map(rect => rect.right))
					const top = Math.min(...rects.map(rect => rect.top))
					const bottom = Math.max(...rects.map(rect => rect.bottom))

					// clientLeft/clientTop carry the border plus any scrollbar on that side —
					// an RTL vertical scrollbar sits on the left — so these are the port's edges.
					const box = el.getBoundingClientRect()
					const portLeft = box.left + el.clientLeft
					const portRight = portLeft + el.clientWidth
					const portTop = box.top + el.clientTop
					const style = getComputedStyle(el)
					const inlineStart = paddingPx(style.scrollPaddingInlineStart, el.clientWidth)
					const inlineEnd = paddingPx(style.scrollPaddingInlineEnd, el.clientWidth)

					// RTL runs the inline axis right to left: mirror it, solve, mirror back.
					const inline =
						style.direction === 'rtl'
							? -nearest(-right, -left, inlineStart - portRight, -portLeft - inlineEnd)
							: nearest(left, right, portLeft + inlineStart, portRight - inlineEnd)
					const block = nearest(
						top,
						bottom,
						portTop + paddingPx(style.scrollPaddingBlockStart, el.clientHeight),
						portTop + el.clientHeight - paddingPx(style.scrollPaddingBlockEnd, el.clientHeight),
					)
					return {
						left: this.direction === 'vertical' ? 0 : inline,
						top: this.direction === 'horizontal' ? 0 : block,
					}
				}),
				filter(delta => delta.left !== 0 || delta.top !== 0),
				tap(delta => el.scrollBy({ ...delta, behavior: reducedMotion$.value ? 'instant' : 'smooth' })),
			),
		)
			.pipe(takeUntil(this.disconnecting$))
			.subscribe()

		// DEV-only structural guard (see `findUnreachableOverflow` above) — an
		// initial pass once this render settles, then re-checked whenever the
		// region's own subtree mutates (content pasted/typed, phase switched).
		if (import.meta.env.DEV) {
			merge(
				timer(0),
				fromMutations(el, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style'] }),
			)
				.pipe(debounceTime(300), takeUntil(this.disconnecting$))
				.subscribe(() => devWarnUnreachableOverflow(el))
		}
	}

	protected override disconnected() {
		this.disconnecting$.next()
	}

	// A reconnect detaches and re-attaches the box, which loses its scroll
	// position, so the replayed key revealing again is the remount it is.
	protected override reconnected() {
		this.disconnecting$ = new Subject<void>()
		this.subscribe(this.debounce, this.currentName)
	}
}

export const overflowWithin = directive(OverflowWithinDirective)
